import { cn } from '@workspace/ui/lib/utils';
import { Icon } from '@/src/components/ui';
import { useCallback, useRef } from 'react';

type Props = {
  currentW: number;
  currentH: number;
  onResize: (w: number, h: number) => void;
  onDelete?: () => void;
  isMobileScreen?: boolean;
  isDisabled?: boolean;
  isTitleHint?: boolean;
};

const sizes = [
  { w: 1, h: 1 },
  { w: 2, h: 1 },
  { w: 1, h: 2 },
  { w: 2, h: 2 },
];

export const ResizeControls = ({
  currentW,
  currentH,
  onResize,
  onDelete,
  isMobileScreen = false,
  isTitleHint,
  isDisabled = false,
}: Props) => {
  const touchStartRef = useRef(false);
  const isProcessingRef = useRef(false);

  const getRectangleStyle = (w: number, h: number) => {
    const baseSize = 8;
    const maxSize = 16;

    const width = Math.min(baseSize * w, maxSize);
    const height = Math.min(baseSize * h, maxSize);

    return {
      width: `${width}px`,
      height: `${height}px`,
    };
  };

  const handleResize = useCallback(
    (w: number, h: number) => {
      onResize(w, h);
    },
    [onResize],
  );

  const handleDelete = useCallback(() => {
    if (isProcessingRef.current || !onDelete) return;

    isProcessingRef.current = true;
    onDelete();

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 100);
  }, [onDelete]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, action: () => void) => {
      e.preventDefault();
      e.stopPropagation();
      action();
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, action: () => void) => {
      if (touchStartRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      action();
    },
    [],
  );
  console.log({ isTitleHint });

  return (
    <div
      className={cn(
        isMobileScreen
          ? 'flex absolute top-0 translate-x-1/2 right-1/2 bg-black/80 p-1 rounded-md gap-1 z-20 shadow-sm'
          : 'hidden md:group-hover:flex absolute top-1 right-1/2 translate-x-1/2 bg-black/80 p-1 rounded-md gap-1 z-20 shadow-sm',
        'transition-all duration-200 ease-in-out flex-col items-center',
      )}
    >
      <div className="flex gap-1">
        {sizes.map(({ w, h }) => {
          const active = currentW === w && currentH === h * 3;
          return (
            <button
              key={`${w}-${h}`}
              onTouchStart={(e) =>
                handleTouchStart(e, () => handleResize(w, h))
              }
              onMouseDown={(e) => handleMouseDown(e, () => handleResize(w, h))}
              className={cn(
                'w-6 h-6 border rounded flex items-center justify-center',
                'transition-all duration-200 ease-in-out cursor-pointer touch-manipulation',
                active
                  ? 'bg-blue-primary text-black border-none scale-105'
                  : 'bg-transparent text-white border-none hover:bg-gray-800',
              )}
              style={{ touchAction: 'manipulation' }}
            >
              <div
                className={cn(
                  'border rounded-[2px]',
                  'transition-all duration-150 ease-in-out',
                  active ? 'border-black' : 'border-white',
                )}
                style={getRectangleStyle(w, h)}
              />
            </button>
          );
        })}

        {onDelete && (
          <button
            onTouchStart={(e) => handleTouchStart(e, handleDelete)}
            onMouseDown={(e) => handleMouseDown(e, handleDelete)}
            className="w-6 h-6 rounded flex items-center justify-center bg-transparent text-white hover:bg-gray-800"
            style={{ touchAction: 'manipulation' }}
          >
            <Icon name="delete-tile" className="size-6 p-1" />
          </button>
        )}
      </div>

      {isTitleHint && (
        <span className="text-[10px] text-white/80 mt-1 whitespace-nowrap">
          Click on block to edit Title
        </span>
      )}
    </div>
  );
};
